import React, { useState, useEffect, useRef } from "react";
import { Table, Tooltip, Button } from "antd";
import axios from "axios";
import { Link } from "react-router-dom";
import moment from "moment";
import DeleteModal from "../../../components/modelpopup/DeleteModal";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { AddHoliday } from "../../../components/modelpopup/AddHoliday";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Dropdown, DropdownButton } from "react-bootstrap";

const Holidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [refreshData, setRefreshData] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [holidayToEdit, setHolidayToEdit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentYear, setCurrentYear] = useState(moment().year());
  const [startDate, setStartDate] = useState(moment().startOf('year'));
  const [endDate, setEndDate] = useState(moment().endOf('year'));

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const [isFullScreen, setFullScreen] = useState(false);
  const maximizeBtnRef = useRef(null);

  useEffect(() => {
    const handleClick = () => {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
        setFullScreen(true);
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
          setFullScreen(false);
        }
      }
    };

    const cleanup = () => {
      if (isFullScreen && document.exitFullscreen) {
        document.exitFullscreen();
        setFullScreen(false);
      }
    };

    const maximizeBtn = maximizeBtnRef.current;
    maximizeBtn.addEventListener("click", handleClick);

    return () => {
      maximizeBtn.removeEventListener("click", handleClick);
      cleanup();
    };
  }, [isFullScreen]);

  useEffect(() => {
    fetchHolidays();
  }, [refreshData, startDate, endDate]);

  const fetchHolidays = async () => {
    setLoading(true);

    let params = {};
    if (startDate && endDate) {
      params = {
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
      };
    }

    try {
      const response = await axios.get(`${apiUrl}/holidays/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        params: params,
      });
      setHolidays(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching holidays:", error);
    }
  };

  const handleNextYear = () => {
    const nextYear = currentYear + 1;
    setCurrentYear(nextYear);
    setStartDate(moment().year(nextYear).startOf('year'));
    setEndDate(moment().year(nextYear).endOf('year'));
    setRefreshData(!refreshData);
  };

  const handlePrevYear = () => {
    const prevYear = currentYear - 1;
    setCurrentYear(prevYear);
    setStartDate(moment().year(prevYear).startOf('year'));
    setEndDate(moment().year(prevYear).endOf('year'));
    setRefreshData(!refreshData);
  };

  const handleSave = () => {
    setRefreshData(!refreshData);
    setEditModalVisible(false);
    toast.success("Holiday added successfully!", {
      position: "top-right",
    });
  };

  const deleteHoliday = async (group_id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this holiday!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`${apiUrl}/holidays/delete/${group_id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setHolidays(holidays.filter((holiday) => holiday.group_id !== group_id));
        await Swal.fire("Deleted!", "Holiday has been deleted.", "success");
      } catch (error) {
        let errorMessage = "Failed to delete holiday. Please try again later.";
        if (error.response && error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        }
        await Swal.fire("Error!", errorMessage, "error");
      }
    }
  };

  const handleEdit = (record) => {
    setHolidayToEdit(record);
    setEditModalVisible("edit");
  };

  const handleAdd = () => {
    setHolidayToEdit(null);
    setEditModalVisible("add");
  };

  const columns = [
    {
      title: "Holiday",
      dataIndex: "holiday_name",
      key: "holiday_name",
      render: (text, record) => (
        <>
          <Tooltip
            title={
              <div>
                {record.holiday_dates.map((date, index) => (
                  <div key={index}>{new Date(date).toDateString()}</div>
                ))}
              </div>
            }
          >
            <span style={{ cursor: "pointer", textDecoration: "underline" }}>
              {text}
            </span>
          </Tooltip>
          {record.is_optional === 1 && (
            <small
              style={{ display: "block", color: "#6f6c6c", marginTop: "3px" }}
            >
              (Optional Leave)
            </small>
          )}
        </>
      ),
    },
    
    {
      title: "Date",
      dataIndex: "holiday_from",
      render: (text, record) => {
        const from = new Date(record.holiday_from).toDateString();
        const to = new Date(record.holiday_to).toDateString();
        return (
          <div>
            {from} - {to}
          </div>
        );
      },
    },
    {
      title: "Days",
      dataIndex: "total_days",
    },
    {
      title: "Location",
      dataIndex: "location",
      render: () => "All Locations",
    },
    {
      title: "Shift",
      dataIndex: "shift",
      render: () => <span className="font-weight-semibold">All Shifts</span>,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text, record) => (
        <div className="dropdown dropdown-action text-end">
          <Link
            to="#"
            className="action-icon dropdown-toggle"
            data-bs-toggle="dropdown"
            aria-expanded="false"
          >
            <i className="material-icons">more_vert</i>
          </Link>
          <div className="dropdown-menu dropdown-menu-right">
            <Link
              onClick={() => handleEdit(record)}
              className="dropdown-item"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#edit_holiday"
            >
              <i className="fa fa-pencil m-r-5" /> Edit
            </Link>
            <button
              className="dropdown-item"
              onClick={() => deleteHoliday(record.group_id)}
            >
              <i className="fa fa-trash m-r-5" /> Delete
            </button>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
        <div className="page-header page-top-icons">
        <div className="row align-items-center">
            <div className="col-md-2">
            <h5>Holidays</h5>
            </div>
              <div className="col-md-4">
                <div className="text-center week-switcher">
                  <Button className="fa fa-chevron-circle-left" onClick={handlePrevYear} />
                  <Button style={{ margin: '0 1px' }}>
                    <span style={{ margin: '0 1px', fontWeight: 'bold' }}>
                      {startDate.format('MMM DD, YYYY')} - {endDate.format('MMM DD, YYYY')}
                    </span>
                  </Button>
                  <Button className="fa fa-chevron-circle-right" onClick={handleNextYear} />
                </div>
              </div>
              <div className="col-md-6 float-end ms-auto">
                <div className="d-flex title-head">
                  <div className="view-icons">
                    <Link to="#" className="grid-view btn btn-link">
                      <i className="las la-redo-alt" />
                    </Link>
                    <Link
                      to="#"
                      className="list-view btn btn-link"
                      id="collapse-header"
                      ref={maximizeBtnRef}
                    >
                      <i className="las la-expand-arrows-alt" />
                    </Link>
                  </div>
                  <div className="form-sort">
                    <Link
                      to="#"
                      className="list-view btn btn-link"
                      data-bs-toggle="modal"
                      data-bs-target="#export"
                    >
                      <i className="las la-file-export" />
                      Export
                    </Link>
                  </div>
                  <Link
                    to="#"
                    className="btn add-btn custom-btn"
                    data-bs-toggle="modal"
                    data-bs-target="#add_holiday"
                  >
                    <i className="la la-plus-circle custom-icon" /> Add Holidays
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              {loading ? (
                <div id="loader-wrapper-">
                  <div className="loader">
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                    <div className="dot"></div>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table
                    columns={columns}
                    dataSource={holidays}
                    className="table-striped"
                    pagination={false}
                    rowKey={(record, index) => index}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <AddHoliday onSave={handleSave} holidayData={holidayToEdit} />
    </>
  );
};

export default Holidays;
